import { ClerkLoading, ClerkProvider, Show, SignInButton, SignUpButton, UserButton, useClerk, useUser } from "@clerk/react";
import { createRootRoute, Link, Outlet, useMatches, useLocation } from "@tanstack/react-router";
import { useEffect, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { isAdminAuthId } from "@/lib/admin";
import { pathToTab } from "@/lib/conceptRail";
import { stashCheckoutIntent } from "@/lib/checkoutIntent";
import { Toaster } from "sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Inbox, Settings as SettingsIcon } from "lucide-react";
import { useInboxCount } from "@/hooks/useInbox";
import { GuidesCatalogTrigger } from "@/components/guide/GuidesCatalogTrigger";
import { DevUserSwitcher } from "@/components/dev/DevUserSwitcher";
import { GuidesCatalogDrawer } from "@/components/guide/GuidesCatalogDrawer";
import { WalkthroughOverlay } from "@/components/guide/WalkthroughOverlay";
import { QuickStartTour } from "@/components/quickstart/QuickStartTour";
import { AnnouncementModal } from "@/components/announcements/AnnouncementModal";
import { ZoteroImportWizard } from "@/components/settings/ZoteroImportWizard";
import { useZoteroBulkImportModalStore } from "@/stores/zotero-bulk-import-modal-store";
import { SIDE_SHEET_WIDTH, useSideSheetStore } from "@/stores/side-sheet-store";
import { AnnouncementsBell } from "@/components/announcements/AnnouncementsBell";
import { ZoteroSyncBell } from "@/components/zotero-sync/ZoteroSyncBell";
import { IsolationNoticeBanner } from "@/components/shell/IsolationNoticeBanner";
import { FeedbackButton } from "@/components/shell/FeedbackButton";
import { ActingAsBanner } from "@/components/admin/ActingAsBanner";
import { StageSection } from "@/components/welcome/StageSection";
import {
  ConceptMini,
  DiscoveryMini,
  ScoringMini,
  DebateMini,
  ArgumentMini,
} from "@/components/welcome/OrientationMinis";
import { useOnboardingController } from "@/hooks/useOnboardingController";
import { useNewVersionPrompt } from "@/hooks/useNewVersionPrompt";
import { WhereAmIOverlay } from "@/components/where-am-i/WhereAmIOverlay";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

function InboxBadge() {
  const { data } = useInboxCount();
  const count = data?.count ?? 0;
  return (
    <Link to="/dashboard" className="relative" title="Research Inbox">
      <Inbox className="size-5 text-muted-foreground hover:text-foreground transition-colors" />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium leading-none text-white"
          style={{ background: "#f59e0b" }}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function ZoteroImportWizardHost() {
  const { isOpen, close, pendingConceptId } = useZoteroBulkImportModalStore();
  if (!isOpen) return null;
  return <ZoteroImportWizard open onClose={close} defaultConceptId={pendingConceptId} />;
}

export const Route = createRootRoute({
  component: RootLayout,
});

function AuthenticatedApp() {
  // CLERK_KEY is a build-time constant, so this conditional hook call is safe
  // (the branch never changes between renders). When Clerk is not configured
  // (dev mode), there's no ClerkProvider so we can't call useUser().
  const clerkUser = CLERK_KEY ? useUser().user : undefined;
  const isAdmin = isAdminAuthId(clerkUser?.id);

  // Onboarding controller drives canShow + activeSurface for the 5 guidance
  // surfaces. Reads the route's conceptId so per-concept Card dismissal
  // can be evaluated.
  const matches = useMatches();
  const conceptId = matches.reduce<string | undefined>((acc, m) => {
    const params = m.params as Record<string, string | undefined>;
    return acc ?? params?.conceptId;
  }, undefined);
  useOnboardingController(conceptId);

  const location = useLocation();
  const currentTab = pathToTab(location.pathname);
  const sideSheetOpen = useSideSheetStore((s) => s.open);
  useNewVersionPrompt();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background text-foreground">
        <ActingAsBanner />
        <IsolationNoticeBanner />
        <header className="flex items-center justify-between border-b px-6 py-3">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center hover:opacity-80">
              <img
                src="/logo-neon-blk.png"
                alt="PaperChase"
                className="h-14 w-auto dark:hidden"
              />
              <img
                src="/logo-neon-wht.png"
                alt=""
                aria-hidden="true"
                className="hidden h-14 w-auto dark:inline-block"
              />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <>
                <Link
                  to="/admin/usage"
                  search={{ tab: "costs" }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Usage
                </Link>
                <Link
                  to="/admin/users"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Users
                </Link>
                <Link
                  to="/admin/activity"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Activity
                </Link>
                <Link
                  to="/admin/announcements"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Announcements
                </Link>
              </>
            )}
            <InboxBadge />
            <AnnouncementsBell />
            <ZoteroSyncBell />
            {!CLERK_KEY && <DevUserSwitcher />}
            <FeedbackButton />
            <GuidesCatalogTrigger />
            <Link
              to="/settings/integrations"
              aria-label="Settings"
              className="inline-flex items-center"
            >
              <SettingsIcon className="size-5 text-muted-foreground hover:text-foreground transition-colors" />
            </Link>
            {CLERK_KEY && <UserButton />}
          </div>
        </header>
        {/* A right side sheet (Add an angle) reserves its width: the page stays fully visible. */}
        <main className={sideSheetOpen ? "p-6 sm:pr-[504px]" : "p-6"}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <AnnouncementModal />
      <ZoteroImportWizardHost />
      <GuidesCatalogDrawer />
      <WalkthroughOverlay />
      <QuickStartTour />
      <WhereAmIOverlay currentTab={currentTab} conceptId={conceptId ?? null} />
      <Toaster theme="dark" richColors closeButton position="bottom-right"
        offset={sideSheetOpen ? { right: SIDE_SHEET_WIDTH + 16 } : undefined} />
    </TooltipProvider>
  );
}

function AuthLoadingScreen() {
  const [timedOut, setTimedOut] = useState(false);
  const clerk = useClerk();

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, []);

  if (timedOut) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <p className="text-sm text-muted-foreground">
          Sign-in is taking longer than expected.
        </p>
        <p className="max-w-sm text-center text-xs text-muted-foreground">
          This can happen with managed Google accounts if your organization
          hasn't approved this app. Try refreshing, or use a personal Google account.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => { clerk.signOut(); window.location.reload(); }}
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted transition-colors"
          >
            Sign out & retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
      <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      <p className="text-sm text-muted-foreground">Signing in...</p>
    </div>
  );
}

/**
 * Brand palette for the pre-auth landing — lime on near-black, mirroring the
 * marketing landing page. A superset of the Catppuccin `C` keys so it can be
 * passed as `palette` to the shared StageSection / OrientationMinis components
 * (the in-app /welcome page keeps the default Catppuccin look).
 */
const BRAND = {
  bg: "#09090D",
  bg2: "#111117",
  mantle: "#111117",
  surface0: "#26262e",
  overlay0: "#737068",
  text: "#F0EBE0",
  subtext: "#C8C2B8",
  mauve: "#CEFF00", // concept accent → lime
  teal: "#2EC4A0", // supports
  peach: "#E0944A", // evidence → amber
  yellow: "#E0944A", // open question → amber
  green: "#CEFF00", // primary (scores, build, checks) → lime
  red: "#C06070", // challenges / objection → rose
  blue: "#5B9BD6", // extends
  sapphire: "#6FB0C9", // reviews
  lime: "#CEFF00",
  limeHover: "#DDFF5C",
  onLime: "#0e1206",
  border: "rgba(255,255,255,0.08)",
  border2: "rgba(255,255,255,0.14)",
};

const limeHover = {
  onMouseEnter: (e: ReactMouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = BRAND.limeHover;
  },
  onMouseLeave: (e: ReactMouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = BRAND.lime;
  },
};

function PricingCard({
  name,
  price,
  priceSuffix,
  subPrice,
  tagline,
  features,
  accent,
  ctaLabel,
  ctaHref,
  signUpRedirectUrl,
}: {
  name: string;
  price: string;
  priceSuffix?: string;
  subPrice?: string;
  tagline: string;
  features: string[];
  accent?: boolean;
  ctaLabel: string;
  /** When set, the CTA is a link (e.g. mailto) instead of a sign-up modal. */
  ctaHref?: string;
  /** Clerk post-signup redirect for the sign-up CTA (e.g. resume into checkout). */
  signUpRedirectUrl?: string;
}) {
  const btnStyle: CSSProperties = {
    width: "100%",
    padding: "10px 0",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    borderRadius: 8,
    textAlign: "center",
    display: "block",
    textDecoration: "none",
    backgroundColor: accent ? BRAND.lime : "transparent",
    color: accent ? BRAND.onLime : BRAND.text,
    border: accent ? "none" : `1px solid ${BRAND.border2}`,
  };

  return (
    <div
      style={{
        flex: "1 1 220px",
        maxWidth: 300,
        padding: "22px 22px 24px",
        borderRadius: 12,
        backgroundColor: accent ? `${BRAND.lime}0d` : BRAND.mantle,
        border: `1px solid ${accent ? `${BRAND.lime}55` : BRAND.border}`,
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: accent ? BRAND.lime : BRAND.text }}>{name}</div>
      <div style={{ marginTop: 6, display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ fontSize: 30, fontWeight: 700, color: BRAND.text }}>{price}</span>
        {priceSuffix && <span style={{ fontSize: 14, color: BRAND.subtext }}>{priceSuffix}</span>}
      </div>
      <div style={{ fontSize: 11, color: BRAND.overlay0, marginTop: 2, minHeight: 14 }}>{subPrice ?? ""}</div>
      <div style={{ fontSize: 12.5, color: BRAND.subtext, marginTop: 8 }}>{tagline}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {features.map((f) => (
          <li key={f} style={{ display: "flex", gap: 8, fontSize: 12.5, color: BRAND.text, lineHeight: 1.4 }}>
            <span style={{ color: BRAND.lime, flexShrink: 0 }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: "auto" }}>
        {ctaHref ? (
          <a href={ctaHref} style={btnStyle} {...(accent ? limeHover : {})}>
            {ctaLabel}
          </a>
        ) : (
          <SignUpButton
            mode="modal"
            forceRedirectUrl={signUpRedirectUrl}
            signInForceRedirectUrl={signUpRedirectUrl}
          >
            <button
              style={btnStyle}
              onClick={() => stashCheckoutIntent(signUpRedirectUrl)}
              {...(accent ? limeHover : {})}
            >
              {ctaLabel}
            </button>
          </SignUpButton>
        )}
      </div>
    </div>
  );
}

function PreAuthLanding() {
  const [entered, setEntered] = useState(false);

  // Carry an optional ?promo= from a campaign landing URL through Clerk signup
  // into the billing page so checkout auto-applies it.
  const landingPromo =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("promo")
      : null;
  const proRedirect =
    "/settings/billing?checkout=annual" +
    (landingPromo ? `&promo=${encodeURIComponent(landingPromo)}` : "");

  useEffect(() => {
    requestAnimationFrame(() => setEntered(true));
  }, []);

  const scrollToSteps = () =>
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND.bg, color: BRAND.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      {/* Header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: `1px solid ${BRAND.border}`,
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        <img src="/logo-neon-wht.png" alt="PaperChase" style={{ height: 36, width: "auto" }} />
        <SignInButton mode="modal">
          <button
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: BRAND.subtext,
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Sign in
          </button>
        </SignInButton>
      </header>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px" }}>
        {/* Hero */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 56,
            opacity: entered ? 1 : 0,
            transform: entered ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.7s ease",
          }}
        >
          <div
            style={{
              display: "inline-block",
              padding: "4px 12px",
              marginBottom: 16,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: BRAND.lime,
              border: `1px solid ${BRAND.lime}55`,
              borderRadius: 999,
            }}
          >
            Now in beta
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.25, margin: "0 0 14px", maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            From literature to <span style={{ color: BRAND.lime }}>argument</span>.
          </h1>

          <p style={{ fontSize: 15, lineHeight: 1.6, color: BRAND.subtext, maxWidth: 500, margin: "0 auto 28px" }}>
            The research workspace that maps what the field argues — and helps you build
            what you argue. PaperChase holds your corpus, scores it against your question,
            maps the debate, and turns your evidence into a defensible argument.
            Start from a question, or bring a draft or library you already have.
          </p>

          {/* Above-the-fold CTA */}
          <SignUpButton mode="modal">
            <button
              style={{
                padding: "14px 32px",
                fontSize: 15,
                fontWeight: 600,
                backgroundColor: BRAND.lime,
                color: BRAND.onLime,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
              {...limeHover}
            >
              Start free →
            </button>
          </SignUpButton>

          {/* Two on-ramps */}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 14 }}>
            {["Start from a question", "Import what I have"].map((label) => (
              <SignUpButton key={label} mode="modal">
                <button
                  style={{
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                    color: BRAND.text,
                    background: "transparent",
                    border: `1px solid ${BRAND.border2}`,
                    borderRadius: 999,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              </SignUpButton>
            ))}
          </div>

          <button
            onClick={scrollToSteps}
            style={{
              marginTop: 18,
              fontSize: 13,
              color: BRAND.overlay0,
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            See how it works ↓
          </button>
        </div>

        {/* Stages — mirrors the marketing site's four-phase arc (Frame / Curate / Analyze / Build) */}
        <div id="how-it-works" style={{ scrollMarginTop: 24 }}>
          <StageSection palette={BRAND} number={1} subtitle="Frame" title="Start with your research question" description="Tell PaperChase what you're actually investigating — a question and a framing, not a keyword. A concept isn't a folder, it's a focused lens: the context that makes every downstream judgment meaningful. Already mid-project? Paste your abstract and it infers the concept for you." color={BRAND.lime}>
            {(visible) => <ConceptMini visible={visible} palette={BRAND} />}
          </StageSection>

          <StageSection palette={BRAND} number={2} subtitle="Curate" title="A corpus scored to your question, not the topic" description="Bring in a few seed papers — or your whole Zotero library — and trawl the citation graph outward. Every paper is read against your concept and classified as supporting, extending, reviewing, or challenging your position before you open it. Dismiss what doesn't belong; the Living Literature Monitor keeps new work arriving, pre-scored, after you've moved on." color={BRAND.lime}>
            {(visible) => (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <DiscoveryMini visible={visible} palette={BRAND} />
                <ScoringMini visible={visible} palette={BRAND} />
              </div>
            )}
          </StageSection>

          <StageSection palette={BRAND} number={3} subtitle="Analyze" title="See where the literature stands" description="Read with the field's debate map open — camps, stances, and tensions, and where your work fits. Ask your corpus questions in plain language and get cited answers. Coverage Audit flags where your evidence is thin, or fly over the whole field as a 2-D landscape." color={BRAND.lime}>
            {(visible) => <DebateMini visible={visible} palette={BRAND} />}
          </StageSection>

          <StageSection palette={BRAND} number={4} subtitle="Build" title="Construct your argument — not just a reading list" description="This is the destination. Link PDF excerpts to claims in the Evidence Map, stress-test your reasoning with Argument Critique, then draft prose in the Writing Workspace and export to DOCX. Bringing a finished draft? Outline Import turns it into a claim map, matched to your corpus." color={BRAND.lime} isLast>
            {(visible) => <ArgumentMini visible={visible} palette={BRAND} />}
          </StageSection>
        </div>

        {/* Pricing */}
        <div
          style={{
            marginTop: 64,
            opacity: entered ? 1 : 0,
            transition: "opacity 0.8s ease 0.8s",
          }}
        >
          <div style={{ textAlign: "center", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: BRAND.overlay0, marginBottom: 18, fontWeight: 600 }}>
            Simple pricing
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", alignItems: "stretch" }}>
            <PricingCard
              name="Free"
              price="$0"
              tagline="The whole workflow, in miniature — one project, forever."
              ctaLabel="Start free"
              features={[
                "1 concept — delete & reframe anytime",
                "Citation trawling & relevance scoring",
                "1 Field Debate build / month",
                "Evidence Map, notes & corpus chat",
                "Zotero import & PDF retrieval",
              ]}
            />
            <PricingCard
              name="Pro"
              price="$19.20"
              priceSuffix="/mo"
              subPrice="billed annually, or $24/mo monthly"
              tagline="Everything a serious researcher needs."
              accent
              ctaLabel="Upgrade to Pro"
              signUpRedirectUrl={proRedirect}
              features={[
                "Unlimited concepts & scoring",
                "4 Field Debate builds + 20 refreshes / mo",
                "Unlimited Evidence Maps",
                "Argument Critique",
                "Literature Landscape & Living Literature Monitor",
                "Writing Workspace + Word export",
              ]}
            />
          </div>
          <p style={{ textAlign: "center", fontSize: 12, color: BRAND.overlay0, marginTop: 14 }}>
            Start free — upgrade to Pro anytime. Evaluating for a lab or library? Institutional
            licensing arrives in 2027 —{" "}
            <a
              href="mailto:hello@paperchase.ai?subject=PaperChase%20for%20my%20institution"
              style={{ color: BRAND.subtext }}
            >
              hello@paperchase.ai
            </a>
            .
          </p>
        </div>

        {/* CTA */}
        <div
          style={{
            textAlign: "center",
            marginTop: 48,
            opacity: entered ? 1 : 0,
            transition: "opacity 0.8s ease 1s",
          }}
        >
          <SignUpButton mode="modal">
            <button
              style={{
                padding: "14px 36px",
                fontSize: 15,
                fontWeight: 600,
                backgroundColor: BRAND.lime,
                color: BRAND.onLime,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
              {...limeHover}
            >
              Start free →
            </button>
          </SignUpButton>
          <p style={{ fontSize: 12, color: BRAND.overlay0, marginTop: 10 }}>No credit card required</p>
          <div style={{ marginTop: 12 }}>
            <SignInButton mode="modal">
              <button
                style={{
                  fontSize: 13,
                  color: BRAND.overlay0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Already have an account? Sign in
              </button>
            </SignInButton>
          </div>
        </div>

        {/* Data attribution — S2's dataset licence (ODC-BY) requires it. */}
        <p style={{ textAlign: "center", fontSize: 12, color: BRAND.overlay0, marginTop: 40 }}>
          Paper data from{" "}
          <a href="https://www.semanticscholar.org" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.subtext }}>
            Semantic Scholar
          </a>{" "}
          (
          <a href="https://opendatacommons.org/licenses/by/1-0/" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.subtext }}>
            ODC-BY
          </a>
          ) and{" "}
          <a href="https://openalex.org" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.subtext }}>
            OpenAlex
          </a>{" "}
          (CC0).
        </p>
      </div>
    </div>
  );
}

function RootLayout() {
  // When Clerk is not configured (local dev), skip the auth gate.
  // ?landing=1 previews the pre-auth landing, which otherwise only renders
  // with a Clerk key + signed-out session (i.e. never in local dev).
  if (!CLERK_KEY) {
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).has("landing")) {
      // Dummy key (a valid-format pk_test_ for a nonexistent instance) so the
      // Sign{In,Up}Buttons can mount; auth is inert in this preview.
      return (
        <ClerkProvider publishableKey="pk_test_ZXhhbXBsZS5jbGVyay5hY2NvdW50cy5kZXYk">
          <PreAuthLanding />
        </ClerkProvider>
      );
    }
    return <AuthenticatedApp />;
  }

  // Public shared routes bypass auth entirely
  if (window.location.pathname.startsWith("/shared/")) {
    return <Outlet />;
  }

  return (
    <>
      <Show when="signed-in">
        <AuthenticatedApp />
      </Show>
      <Show when="signed-out">
        <PreAuthLanding />
      </Show>
      <ClerkLoading>
        <AuthLoadingScreen />
      </ClerkLoading>
    </>
  );
}
